import React, { useEffect, useRef, useState } from "react";
import { Card, Typography, Row, Col, Switch } from "antd";
import styled from "styled-components";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, Sector } from "recharts";
import color from "utils/constants/color";
import { useReactToPrint } from "react-to-print";
import { IconButton } from '@mui/material';
import { PrintOutlined } from '@mui/icons-material'
import Loading from "components/Loading";
const { Title, Text } = Typography;

const colorMap = [
  '#003366',
  '#f58d1d',
  '#40a9ff',
  '#a084ca'
];
const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
  @media print {
    flex-direction: column !important;
    align-items: center !important;
    gap: 5px !important;
    margin-top: 2px !important;
    margin-bottom: 2px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
    page-break-after: auto !important;
    page-break-before: auto !important;
    orphans: 1;
    widows: 1;
  }
`;


const ChartContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0;
  width: 100%;
  // max-width: 550px;
  @media print {
    max-width: 100% !important;
    width: 100% !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
    page-break-after: auto !important;
    flex: 0 0 auto !important;
    margin-bottom: 5px !important;
  }
`;

const LegendContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  padding-left: 16px;
  @media print {
    margin-top: 0 !important;
    padding-left: 0 !important;
    width: 100% !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
    page-break-before: auto !important;
    orphans: 1;
    widows: 1;
    flex: 1 1 auto !important;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) => props.color};
  border: 2px solid ${color.indigo100};
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 12px;
  color: white;
  font-weight: bold;
  @media print {
    break-inside: avoid;
    page-break-inside: avoid;
    page-break-after: auto;
    padding: 4px 6px !important;
    margin-bottom: 5px !important;
    font-size: 0.75em !important;
    line-height: 1.2 !important;
    orphans: 1;
    widows: 1;
  }
`;

const StyledCard = styled(Card)`
  background-color: #f5f5ff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  @media print {
    padding: 15px !important;
    margin: 10px !important;
    min-height: 0 !important;
    height: auto !important;
    page-break-after: auto !important;
    page-break-before: auto !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
    orphans: 1;
    widows: 1;
    box-shadow: none !important;
    border: none !important;
  }
`;
const PrintButton = styled(IconButton)`
  position:absolute !important;
  right:20px;
  top: 15px;
  background-color:#c4c4e6b8 !important;
  @media print {
    visibility: hidden !important;
    display: none !important;
  }
`;

const LanguageToggleContainer = styled.div`
  position: absolute;
  top: 18px;
  right: ${(props) => (props.$hasPrintButton ? "80px" : "20px")};
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffffcc;
  border-radius: 999px;
  padding: 4px 10px;
  z-index: 2;
  @media print {
    visibility: hidden !important;
    display: none !important;
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 1rem 0;
  color: #4b5563;
  font-size: 0.875rem;
  @media print {
    padding: 0.2rem 0 !important;
    font-size: 0.65rem !important;
    margin-top: 2px !important;
  }
`;

const StyledPrintRow = styled(Row)`
  @media print {
    display: flex !important;
    flex-wrap: wrap !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 2px !important;
    margin-top: 0 !important;
  }
  &.print-row-second {
    @media print {
      margin-top: 0 !important;
    }
  }
`;

const StyledPrintCol = styled(Col)`
  @media print {
    flex: 0 0 auto !important;
    max-width: none !important;
    width: auto !important;
  }
`;

const StyledTitle = styled(Title)`
  @media print {
    margin-bottom: 10px !important;
    margin-top: 0 !important;
    font-size: 14px !important;
    line-height: 1.1 !important;
  }
`;

const renderActiveShape = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
  value,
  isMobile,
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * (midAngle ?? 1));
  const cos = Math.cos(-RADIAN * (midAngle ?? 1));
  const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
  const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
  const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
  const my = (cy ?? 0) + ((outerRadius ?? 0) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      {/* <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
        {isMobile ? payload.name.substring(0, 6) + "..." : payload.name.length < 20 ? payload.name : payload.name.substring(0, 15) + "..."}
      </text> */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 6}
        outerRadius={(outerRadius ?? 0) + 10}
        fill={fill}
      />
    </g>
  );
};



const CopyrightNotice = () => {
  const currentYear = new Date().getFullYear();

  return <Footer>&copy; {currentYear} 1WayEducation. All rights reserved.</Footer>;
};

const OverAllGraph = ({ overAllData , is_student_data }) => {
  const { total_atampt, total_completed, total_uncompleted, max_marks, average_marks, grades_by_levels , levels:levelsConfig } = overAllData;
  const [printLoader, setPrintloader] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 500);
  const [isMobile, setIsMobile] = useState(windowWidth < 800);
  const [useEnglishLegend, setUseEnglishLegend] = useState(false);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setIsMobile(windowWidth < 800);
  }, [windowWidth]);
  const pieInnerRadius = windowWidth < 800 ? 30 : 70;
  const pieOuterRadius = windowWidth < 800 ? 50 : 100;
  const imageRef = useRef();

  const chartData = Object.entries(grades_by_levels||{}).map(([level, count],index) => {
    const levelData = levelsConfig.find((lvl) => lvl.title.startsWith(level)) || {};
    return {
      name: `${level}: ${count}`,
      value: count,
      fill: levelData.color || colorMap[index] ||  "#ccc",
    };
  });

  const hasChartData = chartData.some(item => item.value > 0);
  const hasEnglishLegend = Array.isArray(levelsConfig) && levelsConfig.some(
    (level) => typeof level.legend_english === "string" && level.legend_english.trim()
  );

  const chartConfig = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      spacing: [10, 0, 10, 0],
    },
    title: {
      text: null,
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          style: {
            color: "#000",
            textOutline: "none",
            fontWeight: "bold",
            whiteSpace: "wrap",
          },
          format: "{point.name}",
          inside: true,
        //   distance: -80,
          crop: false,
          overflow: "auto",
        },
      },
    },
    series: [
      {
        name: "Percentage",
        colorByPoint: true,
        data: chartData,
      },
    ],
  };
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: '8px',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: payload[0].payload.fill
        }}>
          <p style={{ margin: '0px !important', padding: '0px !important' }}>{`${data.name}: ${percentage}%`}</p>
        </div>
      );
    }
    return null;
  };

    const printCall = useReactToPrint({
        contentRef: imageRef,
        onBeforeGetContent: async () => {
            setPrintloader(true)
            if (imageRef.current) {
                imageRef.current.style.height = "auto";
                imageRef.current.style.overflow = "visible";
                imageRef.current.style.maxHeight = "none";
            }
            await new Promise(resolve => setTimeout(resolve, 500));
        },
        onBeforePrint: async () => {
            setPrintloader(false);
            if (imageRef.current) {
                imageRef.current.style.height = "";
                imageRef.current.style.overflow = "";
                imageRef.current.style.maxHeight = "";
            }
        }
    });
  return (
    <>
      <style>{`
        @media print {
          @page {
            margin: 1cm !important;
            size: letter;
          }
          body, html {
            margin: 0 !important;
            padding: 0 !important;
          }
          * {
            page-break-inside: auto !important;
            page-break-before: auto !important;
            page-break-after: auto !important;
          }
          .printable {
            page-break-inside: auto !important;
            break-inside: auto !important;
            page-break-before: auto !important;
            page-break-after: auto !important;
            height: auto !important;
            min-height: 0 !important;
            margin: 10px !important;
            padding: 15px !important;
          }
          .chartDataUpr {
            margin-bottom: 0 !important;
            margin-top: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
          }
          .chartDataRght {
            width: 100% !important;
            max-width: 100% !important;
          }
          .ant-card-body {
            padding: 15px !important;
            height: auto !important;
            min-height: 0 !important;
          }
          .chartDataTitle {
            margin-bottom: 8px !important;
            margin-top: 0 !important;
          }
          .print-row-second {
            margin-bottom: 5px !important;
          }
          .legendItemCstm {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
    <StyledCard className="printable" ref={imageRef}>
      {hasEnglishLegend ? (
        <LanguageToggleContainer $hasPrintButton={!is_student_data}>
          <Text style={{ fontSize: 12, color: useEnglishLegend ? "#8c8c8c" : "#111" }}>ES</Text>
          <Switch size="small" checked={useEnglishLegend} onChange={setUseEnglishLegend} />
          <Text style={{ fontSize: 12, color: useEnglishLegend ? "#111" : "#8c8c8c" }}>EN</Text>
        </LanguageToggleContainer>
      ) : null}
     { !is_student_data?<>
          <PrintButton onClick={() => printCall()}>
              {printLoader ? <Loading size={20} /> : <PrintOutlined style={{ color:"#000" }} />}
          </PrintButton>
      <StyledTitle className="chartDataTitle" level={4}>Interpretación de resultados globales:</StyledTitle>
        <StyledPrintRow gutter={[16, 16]}>
          <StyledPrintCol xs={24} sm={24} md={24} lg={6}><Text strong>Total attempts: </Text> {total_atampt}</StyledPrintCol>
          <StyledPrintCol xs={24} sm={24} md={24} lg={6}><Text strong>Total completed exams: </Text> {total_completed}</StyledPrintCol>
          <StyledPrintCol xs={24} sm={24} md={24} lg={6}><Text strong>Total uncompleted exams: </Text> {total_uncompleted}</StyledPrintCol>
          <StyledPrintCol xs={24} sm={24} md={24} lg={6}><Text strong>Average district score: </Text> {average_marks}</StyledPrintCol>
        </StyledPrintRow>
        <StyledPrintRow gutter={[16, 16]} className="print-row-second">
          <StyledPrintCol xs={24} sm={24} md={24} lg={12}><Text strong>Maximum possible points: </Text> {max_marks}</StyledPrintCol>
          
        </StyledPrintRow>
      </>:null}
      <Container className="chartDataUpr">
        
        <ChartContainer className="chartDataLeft">
          {hasChartData ? (

            <ResponsiveContainer width="100%" height={500}>
              <PieChart>
                <Pie
                  activeShape={(props) => renderActiveShape({ ...props, isMobile })}
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={pieInnerRadius}
                  outerRadius={pieOuterRadius}
                  dataKey="value"
                  labelLine={false}
                  gap={10}
                  hoverAnimation={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip  />} />
                <Legend 
                  formatter={(value, entry) => {
                    const total = chartData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? ((entry.payload.value / total) * 100).toFixed(1) : 0;
                    return `${value} (${percentage}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: 300,
              color: '#666',
              fontSize: '16px'
            }}>
              No proficiency data available
            </div>
          )}
        </ChartContainer>

        <LegendContainer className="chartDataRght">
          {levelsConfig?.map((level, index) => (
            <>
            {/* {index === 1 && ( <div className="legendItemCstmFirst"></div> )} */}
            <LegendItem className="legendItemCstm" color={level.color}>
              <div>
                <Text style={{ color: "#fff" }}>
                  {useEnglishLegend && level.legend_english ? level.legend_english : level.legend}
                </Text>
              </div>
            </LegendItem>
            </>
          ))}
        </LegendContainer>
      </Container>
     {!is_student_data?<CopyrightNotice/>:null}
    </StyledCard>
    </>
  );
};

export default OverAllGraph;
